import { useMemo } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { Check, Clock, Loader2, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ApprovalGrid } from '@/components/ApprovalGrid';
import type { ApprovalRow } from '@/components/ApprovalGrid';
import { DashboardLoadingState } from '@/components/DashboardLoadingState';
import type { Timesheet } from '@/types/timesheet';

interface ApprovalQueueCardProps {
  isLoading: boolean;
  rows: ApprovalRow[];
  onSelectionChange: (rows: ApprovalRow[]) => void;
  clearSelection: boolean;
  onView: (timesheet: Timesheet) => void;
  onApprove: (timesheet: Timesheet) => void;
  onReject: (timesheet: Timesheet) => void;
  approvingIds: Set<number>;
  rejectingIds: Set<number>;
  canApprove: () => boolean;
  canReject: () => boolean;
  canBulkApprove: () => boolean;
  canViewPolicyViolations: () => boolean;
  canViewDetectedIssues: () => boolean;
  currentEmployeeId: number;
  pendingCount: number;
  overtimeEnabled: boolean;
  overtimeTypes: Array<{ id: number; name: string; badgeColour: string }>;
  departments: string[];
  departmentFilter: string;
  setDepartmentFilter: Dispatch<SetStateAction<string>>;
  anomalyFilter: string;
  setAnomalyFilter: Dispatch<SetStateAction<string>>;
  overdueFilter: boolean;
  setOverdueFilter: Dispatch<SetStateAction<boolean>>;
  overdueCount: number;
  selectedIds: Set<number>;
  onBulkApprove: () => void;
  onBulkReject: () => void;
  isBulkProcessing: boolean;
  isBulkApproving: boolean;
  isBulkRejecting: boolean;
}

export function ApprovalQueueCard({
  isLoading,
  rows,
  onSelectionChange,
  clearSelection,
  onView,
  onApprove,
  onReject,
  approvingIds,
  rejectingIds,
  canApprove,
  canReject,
  canBulkApprove,
  canViewPolicyViolations,
  canViewDetectedIssues,
  currentEmployeeId,
  pendingCount,
  overtimeEnabled,
  overtimeTypes,
  departments,
  departmentFilter,
  setDepartmentFilter,
  anomalyFilter,
  setAnomalyFilter,
  overdueFilter,
  setOverdueFilter,
  overdueCount,
  selectedIds,
  onBulkApprove,
  onBulkReject,
  isBulkProcessing,
  isBulkApproving,
  isBulkRejecting,
}: ApprovalQueueCardProps) {
  // ── Memoized ApprovalGrid slots ─────────────────────────────────────────
  // Inline JSX props would be new references on every render and defeat the
  // React.memo around ApprovalGrid.
  const gridCustomFilters = useMemo(() => (
    <div className="flex items-center gap-2 flex-wrap" data-tour="approvals-filter">
      <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
        <SelectTrigger className="w-[160px] h-9">
          <SelectValue placeholder="Department" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Departments</SelectItem>
          {departments.map(dept => (
            <SelectItem key={dept} value={dept}>{dept}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select value={anomalyFilter} onValueChange={setAnomalyFilter}>
        <SelectTrigger className="w-[140px] h-9">
          <SelectValue placeholder="Issues" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Items</SelectItem>
          <SelectItem value="with-anomalies">With Issues</SelectItem>
          <SelectItem value="clean">No Issues</SelectItem>
        </SelectContent>
      </Select>

      <Button
        variant={overdueFilter ? 'destructive' : 'outline'}
        size="sm"
        onClick={() => setOverdueFilter(f => !f)}
        className="gap-1.5 h-9 whitespace-nowrap"
      >
        <Clock className="h-3.5 w-3.5" />
        Overdue
        {overdueCount > 0 && (
          <span className={`ml-0.5 rounded-full px-1.5 py-0.5 text-xs font-semibold leading-none ${
            overdueFilter ? 'bg-white/20 text-white' : 'bg-destructive/15 text-destructive'
          }`}>
            {overdueCount}
          </span>
        )}
      </Button>
    </div>
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [departmentFilter, departments, anomalyFilter, overdueFilter, overdueCount]);

  const gridToolbarActions = useMemo(() => (
    selectedIds.size > 0 ? (
      <div className="flex items-center gap-2">
        <Button
          onClick={onBulkApprove}
          disabled={!canBulkApprove() || isBulkProcessing}
          title={!canBulkApprove() ? "You don't have permission to bulk approve" : undefined}
          size="sm"
          className="gap-1.5"
          data-tour="approvals-approve-btn"
        >
          {isBulkApproving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
          {isBulkApproving ? 'Approving…' : `Approve Selected (${selectedIds.size})`}
        </Button>
        <Button
          variant="destructive"
          onClick={onBulkReject}
          disabled={!canBulkApprove() || !canReject() || isBulkProcessing}
          title={!canBulkApprove() ? "You don't have permission to bulk approve or reject" : !canReject() ? "You don't have permission to reject timesheets" : undefined}
          size="sm"
          className="gap-1.5"
          data-tour="approvals-reject-btn"
        >
          {isBulkRejecting ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4" />}
          {isBulkRejecting ? 'Rejecting…' : `Reject Selected (${selectedIds.size})`}
        </Button>
      </div>
    ) : null
  ), [selectedIds, onBulkApprove, onBulkReject, canBulkApprove, canReject, isBulkProcessing, isBulkApproving, isBulkRejecting]);

  const gridFilterChips = useMemo(() => (
    (departmentFilter !== 'all' || anomalyFilter !== 'all' || overdueFilter) ? (
      <div className="flex items-center gap-2 flex-wrap">
        {departmentFilter !== 'all' && (
          <Badge
            variant="secondary"
            className="gap-1 cursor-pointer pr-1"
            onClick={() => setDepartmentFilter('all')}
          >
            Dept: {departmentFilter}
            <X className="h-3 w-3 ml-0.5" />
          </Badge>
        )}
        {anomalyFilter !== 'all' && (
          <Badge
            variant="secondary"
            className="gap-1 cursor-pointer pr-1"
            onClick={() => setAnomalyFilter('all')}
          >
            {anomalyFilter === 'with-anomalies' ? 'With Issues' : 'No Issues'}
            <X className="h-3 w-3 ml-0.5" />
          </Badge>
        )}
        {overdueFilter && (
          <Badge
            variant="destructive"
            className="gap-1 cursor-pointer pr-1"
            onClick={() => setOverdueFilter(false)}
          >
            Overdue Only
            <X className="h-3 w-3 ml-0.5" />
          </Badge>
        )}
        <Button
          variant="ghost"
          size="sm"
          className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground"
          onClick={() => {
            setDepartmentFilter('all');
            setAnomalyFilter('all');
            setOverdueFilter(false);
          }}
        >
          Clear all
        </Button>
      </div>
    ) : null
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [departmentFilter, anomalyFilter, overdueFilter]);

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="pb-0">
          <CardTitle>Pending Approvals</CardTitle>
        </CardHeader>
        <CardContent className="pt-6 pb-3 px-6">
          <DashboardLoadingState message="Loading approvals..." />
        </CardContent>
      </Card>
    );
  }

  return (
    /* ── Flat approvals queue — delegated rows show "Delegated From" column ── */
    <Card data-tour="approvals-pending-list">
      <CardHeader className="pb-0">
        <CardTitle>Pending Approvals</CardTitle>
      </CardHeader>
      <CardContent className="pt-6 pb-3 px-6">
        <ApprovalGrid
          rows={rows}
          onSelectionChange={onSelectionChange}
          clearSelection={clearSelection}
          onView={onView}
          onApprove={onApprove}
          onReject={onReject}
          approvingIds={approvingIds}
          rejectingIds={rejectingIds}
          canApprove={canApprove}
          canReject={canReject}
          canBulkApprove={canBulkApprove}
          canViewPolicyViolations={canViewPolicyViolations}
          canViewDetectedIssues={canViewDetectedIssues}
          currentEmployeeId={currentEmployeeId}
          pendingCount={pendingCount}
          overtimeEnabled={overtimeEnabled}
          overtimeTypes={overtimeTypes}
          customFilters={gridCustomFilters}
          toolbarActions={gridToolbarActions}
          filterChips={gridFilterChips}
        />
      </CardContent>
    </Card>
  );
}
